import React, { useState, useEffect } from "react";
import AdminServices from "services/AdminServices";
import ChatHistoryServices from "services/ChatHistoryServices";
import PageTitle from "components/Typography/PageTitle";
import TableLoading from "components/preloader/TableLoading";
import Somethingwentwrong from "pages/Somethingwentwrong";
import {
  Card,
  CardBody,
  Select,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  Button,
} from "@windmill/react-ui";
import sidebar from "routes/sidebar";
import LabelArea from "components/form/LabelArea";
import NotFound from "components/table/NotFound";
import { FiRefreshCcw } from "react-icons/fi";
import InputSearchDate from "components/form/InputSearchDate";
import ChatHistoryTable from "components/languageDriver/ChatHistoryTable";
import ChatMessagesDrawer from "components/drawer/ChatMessagesDrawer";

// Same admin/permission gate ChatHistory.js uses, on its own id_tab so it can
// be granted separately from "chat history" (that page only shows the
// logged-in admin's own chats; this one lets an admin look up any employee).
// Accepts "chat" too: the sidebar's "Chat" menu is granted as id_tab "chat",
// so checking only "chats" hid this page from staff who can see the menu.
const PERMISSION_TABS = ["chats", "chat"];

const EmployeeChats = () => {
  const adminInfo = localStorage.getItem("adminInfo")
    ? JSON.parse(localStorage.getItem("adminInfo"))
    : null;
  // Admin (profile "Admin") sees every employee's chats; everyone else sees
  // only their own. The chat API enforces the same rule from the login token.
  const isAdmin =
    adminInfo?.id === 1 || String(adminInfo?.profile || "").toLowerCase() === "admin";
  const hideView = isAdmin
    ? true
    : adminInfo?.permission?.some(
        (perm) => PERMISSION_TABS.includes(perm.id_tab) && perm.view === 1
      );

  // Bumped on every Search / Refresh click. The shared isUpdate flag was used
  // before, but nothing on this page resets it to false, so after the first
  // click it stayed true and later clicks never re-ran the fetch.
  const [searchKey, setSearchKey] = useState(0);

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  const [chats, setChats] = useState([]);
  const [status, setStatus] = useState(true);
  const [chatsLoading, setChatsLoading] = useState(false);

  const [searchstartDate, setSearchStartDate] = useState(null);
  const [searchendDate, setSearchEndDate] = useState(null);

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Load the employee list once, the same source the Staff page uses.
  useEffect(() => {
    if (!hideView || !isAdmin) {
      setEmployeesLoading(false);
      return;
    }
    AdminServices.getAllStaff()
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        setEmployees(rows);
      })
      .catch((err) => console.error("Failed to fetch employees:", err))
      .finally(() => setEmployeesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchChats = (employeeId) => {
    if (isAdmin && !employeeId) {
      setChats([]);
      return;
    }
    setChatsLoading(true);
    const payload = {
      from: searchstartDate ? formatDate(searchstartDate) : "",
      to: searchendDate ? formatDate(searchendDate) : "",
    };
    const request = isAdmin
      ? ChatHistoryServices.getChatByEmpId(employeeId, payload)
      : ChatHistoryServices.getMyChats(payload.from, payload.to);
    request
      .then((res) => {
        setStatus(res?.status !== false);
        setChats(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("Failed to fetch employee chats:", err);
        setStatus(false);
      })
      .finally(() => setChatsLoading(false));
  };

  // Re-run whenever the selected employee changes, or Search / Refresh is pressed.
  useEffect(() => {
    if (!hideView) return;
    if (!isAdmin || selectedEmployeeId) {
      fetchChats(selectedEmployeeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeId, searchKey]);

  // Chats in progress right now, across all employees — refreshed on a timer
  // so new/ended chats show up without reloading the page.
  const LIVE_REFRESH_MS = 15000;
  // One drawer for both tables, so it stays open when a live chat ends and
  // drops out of the refreshed Live list.
  const [selectedChat, setSelectedChat] = useState(null);
  const [liveChats, setLiveChats] = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState(false);

  useEffect(() => {
    if (!hideView || !isAdmin) return; // live chats are for admins only
    let cancelled = false;
    const loadLive = () =>
      ChatHistoryServices.getLiveChats()
        .then((res) => {
          if (cancelled) return;
          setLiveError(res?.status === false);
          setLiveChats(Array.isArray(res?.data) ? res.data : []);
        })
        .catch(() => !cancelled && setLiveError(true))
        .finally(() => !cancelled && setLiveLoading(false));
    loadLive();
    const timer = setInterval(loadLive, LIVE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live chat count per employee, for the dropdown.
  const liveCountByEmployee = liveChats.reduce((acc, c) => {
    acc[String(c.employee_id)] = (acc[String(c.employee_id)] || 0) + 1;
    return acc;
  }, {});

  const handleSearch = () => setSearchKey((k) => k + 1);
  // Refresh goes back to the default view: clears the dates and re-fetches,
  // so the route is called without from/to (server default: last 10 days).
  const handleRefresh = () => {
    setSearchStartDate(null);
    setSearchEndDate(null);
    setSearchKey((k) => k + 1);
  };

  return (
    <>
      {hideView && (
        <>
          <PageTitle sidebar={sidebar().sidebar} />
          <ChatMessagesDrawer chat={selectedChat} onClose={() => setSelectedChat(null)} />

          {/* Live chats — every chat in progress right now (admins only) */}
          {isAdmin && (
          <Card className="min-w-0 shadow-xs overflow-hidden bg-white dark:bg-gray-800 mb-5">
            <CardBody>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                  Live chats{" "}
                  <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    ● {liveChats.length}
                  </span>
                </h3>
                <span className="text-xs text-gray-500">Updates every 15 seconds · click a chat to watch it live</span>
              </div>
              {liveLoading ? (
                <TableLoading row={2} col={6} width={190} height={20} />
              ) : liveError ? (
                <p className="text-sm text-gray-500">Could not load live chats.</p>
              ) : liveChats.length === 0 ? (
                <p className="text-sm text-gray-500">No chats in progress right now.</p>
              ) : (
                <TableContainer style={{ maxHeight: "40vh", overflowY: "auto", scrollbarWidth: "thin" }}>
                  <Table>
                    <TableHeader className="custom-table-header capitalize sticky top-0 z-0">
                      <tr>
                        <TableCell className="text-center">Sl.No.</TableCell>
                        <TableCell className="text-center">Chat ID</TableCell>
                        <TableCell className="text-center">Agent Name</TableCell>
                        <TableCell className="text-center">Executive</TableCell>
                        <TableCell className="text-center">Started</TableCell>
                        <TableCell className="text-center">Rating</TableCell>
                        <TableCell className="text-center">Duration</TableCell>
                        <TableCell className="text-center">View</TableCell>
                      </tr>
                    </TableHeader>
                    <ChatHistoryTable isAdmin={isAdmin} historydata={liveChats} selectedChat={selectedChat} onSelectChat={setSelectedChat} />
                  </Table>
                </TableContainer>
              )}
            </CardBody>
          </Card>
          )}

          <Card className="min-w-0 shadow-xs overflow-hidden bg-white dark:bg-gray-800 mb-5">
            <CardBody>
              <div className="flex items-center gap-3 flex-wrap">
                {isAdmin ? (
                <div className="flex items-center gap-2">
                  <LabelArea label="Employee" />
                  <Select
                    className="h-10 text-sm"
                    value={selectedEmployeeId}
                    disabled={employeesLoading}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  >
                    <option value="">
                      {employeesLoading ? "Loading..." : "Select an employee"}
                    </option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                        {liveCountByEmployee[String(emp.id)]
                          ? ` — ● ${liveCountByEmployee[String(emp.id)]} live`
                          : ""}
                      </option>
                    ))}
                  </Select>
                </div>
                ) : (
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 mr-2">My chats</h3>
                )}

                <div className="flex items-center gap-2">
                  <LabelArea label="Start Date" />
                  <div className="w-50">
                    <InputSearchDate
                      value={searchstartDate}
                      onChange={(event) => setSearchStartDate(event)}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <LabelArea label="End Date" />
                  <div className="w-50">
                    <InputSearchDate
                      value={searchendDate}
                      min={searchstartDate}
                      onChange={(event) => setSearchEndDate(event)}
                    />
                  </div>
                </div>

                <Button
                  className="rounded-md h-10 px-8"
                  disabled={isAdmin && !selectedEmployeeId}
                  onClick={handleSearch}
                >
                  Search
                </Button>

                <div>
                  <FiRefreshCcw onClick={handleRefresh} />
                </div>
              </div>
            </CardBody>
          </Card>

          <div className="data-table-container">
            {isAdmin && !selectedEmployeeId ? (
              <NotFound title="Select an employee to see their recent chats." />
            ) : chatsLoading ? (
              <TableLoading row={12} col={6} width={190} height={20} />
            ) : !status ? (
              <Somethingwentwrong title="Something went wrong, please try again later." />
            ) : chats.length > 0 ? (
              <TableContainer
                className="mb-8"
                style={{
                  maxHeight: "65vh",
                  overflowY: "scroll",
                  scrollbarWidth: "thin",
                }}
              >
                <Table>
                  <TableHeader className="custom-table-header capitalize sticky top-0 z-0">
                    <tr>
                      <TableCell className="text-center">Sl.No.</TableCell>
                      <TableCell className="text-center">Chat ID</TableCell>
                      <TableCell className="text-center">Agent Name</TableCell>
                      {/* <TableCell className="text-center">Executive</TableCell> */}
                      <TableCell className="text-center">Started</TableCell>
                      <TableCell className="text-center">Rating</TableCell>
                      <TableCell className="text-center">Duration</TableCell>
                      <TableCell className="text-center">View</TableCell>
                    </tr>
                  </TableHeader>
                  <ChatHistoryTable historydata={chats} selectedChat={selectedChat} onSelectChat={setSelectedChat} />
                </Table>
              </TableContainer>
            ) : (
              <NotFound title={isAdmin ? "This employee has no chats in the selected range." : "You have no chats in the selected range."} />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default EmployeeChats;
